import type { UsageResponse } from '../services/billingService';
import { isPaidMeteredPlan } from './creditUsageDisplay';

/** Stripe metered overage rate: €0.20 per credit. */
export const EUR_PER_OVERAGE_CREDIT = 0.2;

/** Coerce API/DB overage credit values (number or numeric string) for the UI. */
export function coerceOverageCredits(
  credits: number | string | null | undefined
): number | null {
  if (credits == null || credits === '') return null;
  const n = typeof credits === 'number' ? credits : Number(credits);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, n);
}

/** Convert a UI euro budget into credit units stored on the company. */
export function overageCreditsFromEur(eur: number): number {
  const safe = Math.max(0, Number.isFinite(eur) ? eur : 0);
  return safe / EUR_PER_OVERAGE_CREDIT;
}

/** Convert stored overage credits into a euro budget for the UI. */
export function overageEurFromCredits(
  credits: number | string | null | undefined
): number | null {
  const coerced = coerceOverageCredits(credits);
  if (coerced == null) return null;
  return coerced * EUR_PER_OVERAGE_CREDIT;
}

/** Format stored overage credits for display that still matches DB units. */
export function formatOverageCreditsForInput(
  credits: number | string | null | undefined
): string {
  const coerced = coerceOverageCredits(credits);
  if (coerced == null) return '';
  // Avoid long floats from €↔credit round-trips (e.g. 0.3 / 0.2).
  const rounded = Math.round(coerced * 100) / 100;
  return String(rounded);
}

/** Format stored overage credits as a euro budget for the Billing input. */
export function formatOverageEurForInput(
  credits: number | string | null | undefined
): string {
  const euros = overageEurFromCredits(credits);
  if (euros == null) return '';
  return String(Math.round(euros * 100) / 100);
}

/** Metered overage credits billed this period. */
export function resolvedOverageCreditsUsed(usage: UsageResponse): number {
  const explicit = coerceOverageCredits(usage.monthly_overage_credits_used);
  if (explicit != null) return explicit;
  const limit = coerceOverageCredits(usage.overage_credits_limit);
  const remaining = coerceOverageCredits(usage.overage_credits_remaining);
  if (limit != null && remaining != null) return Math.max(0, limit - remaining);
  return 0;
}

/** Settings cap in credits; null means uncapped pay-as-you-go. */
export function resolvedOverageCreditsLimit(
  usage: UsageResponse
): number | null {
  if (usage.overage_credits_limit === null) return null;
  return coerceOverageCredits(usage.overage_credits_limit);
}

export function formatOverageEurAmount(credits: number): string {
  const euros = Math.max(0, credits) * EUR_PER_OVERAGE_CREDIT;
  return (Math.round(euros * 100) / 100).toFixed(2);
}

/** 0–100 against the settings cap; 0 when uncapped. */
export function overageUsagePercent(usage: UsageResponse): number {
  const used = resolvedOverageCreditsUsed(usage);
  const limit = resolvedOverageCreditsLimit(usage);
  if (limit == null || limit <= 0) return 0;
  return Math.min(100, (used / limit) * 100);
}

export function shouldShowOverageUsageMeter(usage: UsageResponse): boolean {
  if (!isPaidMeteredPlan(usage)) return false;
  if (resolvedOverageCreditsUsed(usage) > 0) return true;
  const limit = resolvedOverageCreditsLimit(usage);
  return limit != null && limit > 0;
}
